'use client';

import { useEffect } from 'react';
import { notifyError, startMonitoring } from '@/lib/monitoring/client';
import { wasReportedOnServer } from '@/lib/monitoring/shared';

type GlobalErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

/**
 * The last boundary. It replaces the root layout, so the provider tree may
 * never have mounted — which is why it starts the client itself.
 */
export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    startMonitoring();

    // A digest means the server already reported this through
    // `instrumentation.ts`, with the stack intact. The copy that reaches the
    // browser is sanitised to a bare message; reporting it again files the same
    // incident twice, and the second copy is the useless one.
    if (wasReportedOnServer(error)) return;

    notifyError(error, { boundary: { scope: 'global', digest: error.digest ?? null } });
  }, [error]);

  return (
    <html lang="en" className="h-full antialiased">
      <body className="min-h-full flex flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="text-neutral-600">We could not load this page.</p>
        <button
          type="button"
          onClick={reset}
          className="rounded-lg bg-black px-4 py-2 font-semibold text-white"
        >
          Try again
        </button>
      </body>
    </html>
  );
}
